import MojitoCore from 'mojito/core';
import MojitoPresentation from 'mojito/presentation';
import MojitoModules from 'mojito/modules';
import { useCallback, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import MojitoServices from 'mojito/services';
import Search from 'applications/sports/components/search/index.js';
import SearchOverlay from 'applications/sports/components/search/search-overlay/index.js';

const {
    ContextExtender: AnalyticsContextExtender,
    Context: AnalyticsContext,
    ContextBuilder: AnalyticsContextBuilder,
} = MojitoCore.Presentation.AnalyticsContext;
const { useComponentVisibilityAfterScrollRestoration } = MojitoPresentation.Hooks;
const { NextRaces, QuickStartGuideBanner, QuickStartGuide } = MojitoModules;
const { useMenuContent } = MojitoModules.Common.Hooks;
const { selectTimeZoneGmt } = MojitoServices.UserSettings.selectors;

const { BannerPromotions, MenuLinkPromotions, PromotionUtils, PrebuiltPromotionCards } =
    MojitoModules.Promotions;
const { FlexPane, DocumentHead, GlobalLinks, PageSplitLayout, DefaultHeadMetaFactory, Button } =
    MojitoPresentation.Components;
const EventCardsCarousel = MojitoModules.EventCardsCarousel.module;
const PageContextMenu = MojitoModules.PageContextMenu;
const SportsMenu = MojitoModules.SportsMenu.module;
const TopSports = MojitoModules.TopSports.module;
const AnalyticsTypes = MojitoServices.Analytics.types;
const { HOME_PAGE } = AnalyticsTypes.SOURCE;

const extendAnalyticsContext = (context, value) => ({ ...context, ...value });
const selectionSourceContext = new AnalyticsContextBuilder().withAnalyticsPath(HOME_PAGE).build();

const analyticsContext = {
    marketChangeAnalyticsType: 'homePageEventListMarketSelectorValueChanged',
};

const applicablePage = 'homepage';

const HomePage = props => {
    const {
        mojitoTools: { config, l10n },
    } = props;
    const { leftPromotions: promotionsConfig } = config;
    const metaFactory = config.documentHeadMetaFactory || DefaultHeadMetaFactory;
    const documentHeadMetaFactory = useMemo(() => new metaFactory(l10n), [metaFactory, l10n]);
    const headData = {
        title: '$HOME_PAGE.PAGE_TITLE',
        keywords: '$HOME_PAGE.PAGE_KEYWORDS',
        description: '$HOME_PAGE.PAGE_DESCRIPTION',
        canonical: '$HOME_PAGE.PAGE_CANONICAL',
    };

    const timeZone = useSelector(state => selectTimeZoneGmt(state));
    useMenuContent('', timeZone);

    return (
        <>
            <DocumentHead data={headData} metaFactory={documentHeadMetaFactory} />
            <PageSplitLayout>
                <FlexPane config={config.primaryContentLayout} class="ta-primaryContentLayout">
                    <LayoutBuilder {...props} layoutParts={layoutParts} />
                </FlexPane>

                <FlexPane config={config.secondaryContentContainer}>
                    <PageContextMenu
                        config={config.leftContextMenu}
                        applicablePage={applicablePage}
                    />
                    <SportsMenu config={config.sportsMenu} />
                    <GlobalLinks />

                    {PromotionUtils.hasLocationId(promotionsConfig) && (
                        <BannerPromotions
                            config={promotionsConfig}
                            key={promotionsConfig.locationId}
                            applicablePage={applicablePage}
                        />
                    )}
                </FlexPane>
            </PageSplitLayout>
        </>
    );
};

const layoutParts = {
    TopPromotions: props => <TopPromotionsPart {...props} />,
    Search: props => <SearchPart {...props} />,
    EventCardsCarousel: props => (
        <EventCardsCarousel
            config={props.mojitoTools.config.eventCardsCarousel}
            pageId={applicablePage}
        />
    ),
    QuickStartGuide: props => (props.showQuickStartGuide ? <QuickStartGuide /> : null),
    PageContextMenu: props => (
        <PageContextMenu
            config={props.mojitoTools.config.topContextMenu}
            applicablePage={applicablePage}
        />
    ),
    QuickStartGuideBanner: () => <QuickStartGuideBanner />,
    PrebuiltPromotionCards: props => <PrebuiltPromotionCardsPart {...props} />,
    NextRaces: props => <NextRacesPart {...props} />,
    TopSports: props => <TopSportsPart {...props} />,
};

const LayoutBuilder = props => {
    const {
        layoutParts,
        mojitoTools: { config },
    } = props;
    return Object.entries(config.layoutParts)
        .sort(([, paramsA], [, paramsB]) => paramsA.position - paramsB.position)
        .map(([name, params]) => {
            const Component = layoutParts[name];
            return Component && params.show ? <Component key={name} {...props} /> : null;
        });
};

const TopPromotionsPart = ({ mojitoTools: { appContext, config } }) => {
    const onTopPromotionsLinkClick = menuItem => {
        appContext.analyticsEmitter.emitAnalytics('sportsCarouselLinkClicked', menuItem.id);
    };

    return (
        <FlexPane config={config.topPromotionsContainer}>
            {PromotionUtils.hasLocationId(config.topLinkPromotions) && (
                <MenuLinkPromotions
                    config={config.topLinkPromotions}
                    applicablePage={applicablePage}
                    onLinkClick={onTopPromotionsLinkClick}
                />
            )}
            {PromotionUtils.hasLocationId(config.topBannerPromotions) && (
                <BannerPromotions
                    config={config.topBannerPromotions}
                    applicablePage={applicablePage}
                />
            )}
        </FlexPane>
    );
};

const SearchPart = props => {
    const {
        mojitoTools: { config },
    } = props;
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    return isSearchOpen ? (
        <SearchOverlay isOpen={isSearchOpen} onSearchClose={() => setIsSearchOpen(false)} />
    ) : (
        <Button onClick={() => setIsSearchOpen(true)}>
            <Search config={config.searchControllerView} />
        </Button>
    );
};

const NextRacesPart = props => {
    const { mojitoTools } = props;
    const { config, stringResolver } = mojitoTools;
    const nextRaces = config.nextRacesSports.map(nextRaceConfig => {
        return (
            nextRaceConfig.show && (
                <NextRaces
                    applicablePage={applicablePage}
                    key={nextRaceConfig.sportId}
                    sportId={nextRaceConfig.sportId}
                    config={config.nextRacesController}
                    title={stringResolver.resolveString(
                        `$HOME_PAGE.NEXT_OFF.${nextRaceConfig.sportId.toUpperCase()}`
                    )}
                />
            )
        );
    });

    return nextRaces.some(nextRace => !!nextRace) ? <>{nextRaces}</> : null;
};

const PrebuiltPromotionCardsPart = ({ mojitoTools: { config } }) => (
    <AnalyticsContext.Provider value={selectionSourceContext}>
        <PrebuiltPromotionCards
            applicablePage={applicablePage}
            config={config.prebuiltPromotionCards}
        />
    </AnalyticsContext.Provider>
);

const TopSportsPart = props => {
    const {
        mojitoTools: { config, stringResolver },
    } = props;
    const { primary: primaryCoupon, secondary: secondaryCoupon } = config.topSportCoupons;
    return (
        <AnalyticsContextExtender
            value={analyticsContext}
            extendingStrategy={extendAnalyticsContext}
        >
            <TopSportsContent config={config}>
                {!primaryCoupon.hide && (
                    <TopSports
                        config={primaryCoupon.coupon}
                        navigationTitle={stringResolver.resolveString(
                            '$HOME_PAGE.PRIMARY_TOP_SPORTS_TITLE'
                        )}
                    />
                )}

                {!secondaryCoupon.hide && (
                    <TopSports
                        config={secondaryCoupon.coupon}
                        navigationTitle={stringResolver.resolveString(
                            '$HOME_PAGE.SECONDARY_TOP_SPORTS_TITLE'
                        )}
                    />
                )}
            </TopSportsContent>
        </AnalyticsContextExtender>
    );
};

const TopSportsContent = props => {
    const { config, children } = props;
    const [elementRef, visible] = useTopSportsVisibility(config);
    return (
        <FlexPane
            elementRef={elementRef}
            class="ta-TopSportsSection"
            config={config.topSportsContainer}
        >
            {visible ? children : null}
        </FlexPane>
    );
};

const isElementInViewport = el => {
    if (!el) {
        return false;
    }
    const rect = el.getBoundingClientRect();
    const isInRange = (num, start, end) => {
        const leftInRange = num >= start;
        const rightInRange = num < end;
        return leftInRange && rightInRange;
    };
    return (
        (isInRange(rect.top, 0, window.innerHeight) ||
            isInRange(rect.bottom, 0, window.innerHeight)) &&
        (isInRange(rect.left, 0, window.innerWidth) || isInRange(rect.right, 0, window.innerWidth))
    );
};

const useTopSportsVisibility = config => {
    const [visible, setVisible] = useState(!config.topSportCoupons.optimizeRendering);
    const onShowUp = useCallback(() => setVisible(true), []);
    const visibilityRef = useComponentVisibilityAfterScrollRestoration(onShowUp);
    const elementRef = useCallback(
        element => {
            // We will detect if element is in view port even before onShowUp handler will be called.
            // This is needed to properly report PAGE_READY metric once prefetch content is used in conjunction with optimizeRendering.
            if (isElementInViewport(element)) {
                setVisible(true);
            }
            visibilityRef(element);
        },
        [visibilityRef]
    );
    return [elementRef, visible];
};

HomePage.ALL_SPORTS_CODE = 'ALL';

HomePage.getStyle = config => ({
    margin: config.margin,
    display: 'flex',
    flexDirection: 'column',
});

export default HomePage;
